<script setup lang="ts">
/**
 * A destructive action that cannot fire on one click.
 *
 * BG §9.3 — "anything that leaves the building waits for approval" is the product's whole thesis, and
 * it should hold for the console itself. Deleting a team, revoking a key or cancelling a run used
 * to be a single unguarded click; this is the friction that the form-not-hue treatment leans on,
 * since the palette has no red to warn with (BG §18.2).
 */
import { ref } from 'vue'
import { Button } from './ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog'
import ZhSystemLabel from './ZhSystemLabel.vue'

withDefaults(
    defineProps<{
        /** The button's own label, e.g. `Delete team`. */
        label: string
        /** The dialog heading. Name the thing, so the dialog is not answerable on autopilot. */
        title: string
        /** What will happen, in plain words. Say what is irreversible. */
        description: string
        /** The label on the confirming button. Defaults to the trigger's label. */
        confirmLabel?: string
        disabled?: boolean
        size?: 'sm' | 'default'
        /**
         * An accessible name for the trigger. Several of these sit in one list, where "Delete"
         * alone does not say what would be deleted.
         */
        ariaLabel?: string
    }>(),
    { disabled: false, size: 'sm' },
)

const emit = defineEmits<{ confirm: [] }>()
const open = ref(false)

function confirm(): void {
    open.value = false
    emit('confirm')
}
</script>

<template>
    <Dialog v-model:open="open">
        <Button type="button" :size="size" variant="destructive" :disabled="disabled" :aria-label="ariaLabel" @click="open = true">
            <!-- BG §22.3: the glyph is the channel that survives greyscale, and magenta alone cannot say "danger". -->
            <span aria-hidden="true">⚠</span>
            {{ label }}
        </Button>
        <DialogContent>
            <DialogHeader>
                <DialogTitle>{{ title }}</DialogTitle>
                <DialogDescription>{{ description }}</DialogDescription>
            </DialogHeader>
            <ZhSystemLabel tone="muted">This cannot be undone</ZhSystemLabel>
            <DialogFooter>
                <Button type="button" size="sm" variant="outline" @click="open = false">Cancel</Button>
                <Button type="button" size="sm" variant="destructive" @click="confirm">
                    <span aria-hidden="true">⚠</span>
                    {{ confirmLabel ?? label }}
                </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
